import { Grid, styled, Theme, Tooltip } from "@mui/material";
import AcxChip from "components/UI/AcxChip";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import React from "react";
import {
    ReclassifyJobStatus,
    ReclassifyJobStatusV2,
} from "services/ClassificationService";
import ReclassifyHistoryStore from "./ReclassifyHistoryStore";
import { Refresh } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import { formatUsd } from "components/Admin/Organizations/OrganizationDetail/KboFlowDebug/kboModelPricing";
import {
    dateColumnType,
    dateTimeColumnType,
} from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";

const TooltipList = styled("ul")(({ theme }) => ({
    paddingBlock: 0,
    paddingInline: theme.spacing(2),
}));

function getStatusCellBackgroundStyles(
    status: ReclassifyJobStatus | ReclassifyJobStatusV2,
    theme: Theme,
): React.CSSProperties {
    let backgroundColor: string | undefined = theme.palette.white.main;
    let color: string | undefined = theme.palette.black.main;

    switch (status) {
        case "Running":
        case "Processing":
            backgroundColor = theme.palette.primary[100];
            color = theme.palette.primary.main;
            break;
        case "Failed":
            backgroundColor = theme.palette.error[100];
            color = theme.palette.error[700];
            break;
        case "Completed":
            backgroundColor = "#DAF2EE";
            color = "#239C98";
            break;
        case "Queued":
        case "Scheduling":
        case "Scheduled":
            backgroundColor = theme.palette.gray[200];
            color = theme.palette.gray[600];
            break;
        case "Timeout":
            backgroundColor = "#FEEADC";
            color = "#F97316";
            break;
    }

    return {
        backgroundColor,
        color,
        paddingInline: theme.spacing(1),
        overflow: "hidden",
        textOverflow: "ellipsis",
    };
}

// Columns rendered the same way for V1 and V2 jobs
const classifiersColumn: IColDef = {
    field: "classifiers",
    headerName: "Classifiers",
    flex: 1,
    minWidth: 160,
    renderCell(params) {
        const classifiers = params.value as string[] | undefined;
        if (!classifiers || !classifiers.length) return null;
        const truncateClassifiers = classifiers.length > 2;

        return (
            <Grid container flexWrap="nowrap" columnGap={1}>
                {truncateClassifiers && (
                    <Grid item>
                        <AcxChip
                            size="small"
                            color="lightBlue"
                            label={`${classifiers.length} Classifiers`}
                            customTooltip={
                                <TooltipList>
                                    {classifiers.map((classifier) => (
                                        <li>{classifier}</li>
                                    ))}
                                </TooltipList>
                            }
                        />
                    </Grid>
                )}
                {!truncateClassifiers &&
                    classifiers.map((classifier) => (
                        <Grid item>
                            <AcxChip
                                size="small"
                                color="lightBlue"
                                label={classifier}
                            />
                        </Grid>
                    ))}
            </Grid>
        );
    },
};

const hierarchiesColumn: IColDef = {
    field: "hierarchies",
    headerName: "Hierarchy",
    flex: 1,
    minWidth: 140,
    renderCell(params) {
        if (params.row.allHierarchies) {
            return <AcxChip size="small" color="lightBlue" label="All" />;
        }

        const hierarchies = params.value as string[] | undefined;
        if (!hierarchies || !hierarchies.length) return null;
        const truncateHierarchies = hierarchies.length > 1;

        return (
            <Grid container flexWrap="nowrap" columnGap={1}>
                {truncateHierarchies && (
                    <Grid item>
                        <AcxChip
                            size="small"
                            color="lightBlue"
                            label={`${hierarchies.length} Hierarchies`}
                            customTooltip={
                                <TooltipList>
                                    {hierarchies.map((hierarchy) => (
                                        <li>{hierarchy}</li>
                                    ))}
                                </TooltipList>
                            }
                        />
                    </Grid>
                )}
                {!truncateHierarchies &&
                    hierarchies.map((hierarchy) => (
                        <Grid item>
                            <AcxChip
                                size="small"
                                color="lightBlue"
                                label={hierarchy}
                            />
                        </Grid>
                    ))}
            </Grid>
        );
    },
};

const statusColumn: IColDef = {
    field: "status",
    headerName: "Status",
    flex: 0.5,
    minWidth: 120,
    renderCell(params) {
        if (!params.value) return;

        return (
            <Grid
                container
                item
                sx={(theme) =>
                    getStatusCellBackgroundStyles(
                        params.value!.toString() as
                            | ReclassifyJobStatus
                            | ReclassifyJobStatusV2,
                        theme,
                    )
                }
            >
                {params.value.toString()}
            </Grid>
        );
    },
};

export const reclassifyHistoryColumns: IColDef[] = [
    {
        field: "userName",
        headerName: "User",
        flex: 1,
        minWidth: 200,
    },
    classifiersColumn,
    hierarchiesColumn,
    statusColumn,
    {
        field: "totalRecords",
        headerName: "Total Records",
        flex: 0.5,
        minWidth: 130,
        type: "number",
    },
    {
        field: "startDate",
        headerName: "Date Range Start",
        flex: 0.5,
        minWidth: 150,
        ...dateColumnType,
    },
    {
        field: "endDate",
        headerName: "Date Range End",
        flex: 0.5,
        minWidth: 150,
        ...dateColumnType,
    },
    {
        field: "dateReference",
        headerName: "Date Range Reference",
        flex: 0.5,
        minWidth: 180,
    },
    {
        field: "executionStartDateTimeUtc",
        headerName: "Reclassify Start",
        flex: 1,
        minWidth: 190,
        ...dateTimeColumnType,
    },
    {
        field: "executionEndDateTimeUtc",
        headerName: "Reclassify Complete",
        flex: 1,
        minWidth: 190,
        ...dateTimeColumnType,
    },
    {
        field: "failureReason",
        headerName: "Failure Reason",
        flex: 1,
        minWidth: 250,
    },
    {
        field: "id",
        headerName: "Job ID",
        flex: 0.25,
        minWidth: 300,
    },
];

export const reclassifyHistoryColumnsV2: IColDef[] = [
    {
        field: "userName",
        headerName: "User",
        flex: 1,
        minWidth: 200,
    },
    classifiersColumn,
    hierarchiesColumn,
    statusColumn,
    {
        field: "processedRecords",
        headerName: "Processed Records",
        flex: 0.5,
        minWidth: 150,
        type: "number",
    },
    {
        field: "totalRecords",
        headerName: "Total Records",
        flex: 0.5,
        minWidth: 130,
        type: "number",
    },
    {
        field: "startDate",
        headerName: "Date Range Start",
        flex: 0.5,
        minWidth: 150,
        ...dateColumnType,
    },
    {
        field: "endDate",
        headerName: "Date Range End",
        flex: 0.5,
        minWidth: 150,
        ...dateColumnType,
    },
    {
        field: "dateReference",
        headerName: "Date Range Reference",
        flex: 0.5,
        minWidth: 180,
    },
    {
        field: "createdOn",
        headerName: "Requested",
        flex: 1,
        minWidth: 190,
        ...dateTimeColumnType,
    },
    {
        field: "startedAt",
        headerName: "Reclassify Start",
        flex: 1,
        minWidth: 190,
        ...dateTimeColumnType,
    },
    {
        field: "completedAt",
        headerName: "Reclassify Complete",
        flex: 1,
        minWidth: 190,
        ...dateTimeColumnType,
    },
    {
        field: "failureReason",
        headerName: "Failure Reason",
        flex: 1,
        minWidth: 250,
    },
    {
        field: "id",
        headerName: "Job ID",
        flex: 0.25,
        minWidth: 300,
    },
];

// V2 columns with the estimated cost after Total Records and the estimated tokens at the end
export const reclassifyHistoryColumnsLlm: IColDef[] = [
    ...reclassifyHistoryColumnsV2.flatMap<IColDef>((column) =>
        column.field !== "totalRecords"
            ? [column]
            : [
                  column,
                  {
                      field: "estimatedCostUsd",
                      headerName: "Cost (est.)",
                      flex: 0.5,
                      minWidth: 130,
                      type: "number",
                      renderCell(params) {
                          if (params.value === undefined) return null;
                          const unpricedModels = params.row
                              .unpricedModels as string[];
                          const cost = formatUsd(params.value as number);
                          if (!unpricedModels.length) return cost;

                          return (
                              <Tooltip
                                  title={`Excludes tokens for ${unpricedModels.join(
                                      ", ",
                                  )} (no known price).`}
                              >
                                  <span>{cost}*</span>
                              </Tooltip>
                          );
                      },
                  },
              ],
    ),
    {
        field: "inputTokens",
        headerName: "Input Tokens (est.)",
        flex: 0.5,
        minWidth: 170,
        type: "number",
    },
    {
        field: "cachedInputTokens",
        headerName: "Cached Input Tokens (est.)",
        flex: 0.5,
        minWidth: 210,
        type: "number",
    },
    {
        field: "outputTokens",
        headerName: "Output Tokens (est.)",
        flex: 0.5,
        minWidth: 180,
        type: "number",
    },
];

export function getReclassifyHistoryControls(
    store: ReclassifyHistoryStore,
    loadTask: string,
    onRefresh: () => void,
): React.ReactElement[] {
    return [
        <AcxButton
            onClick={onRefresh}
            sx={() => ({ margin: 0 })}
            color="white"
            loading={store.getTaskLoading(loadTask)}
        >
            <Refresh />
        </AcxButton>,
    ];
}
